import React from 'react';
import { FilterSidebar, RoleRestrictedMenuItem } from '@emfood/shared';

import {
  Box,
  Stack,
  Select,
  MenuItem,
  TextField,
  Typography,
  InputLabel,
  FormControl,
  Autocomplete,
} from '@mui/material';

import { SearchAndFilterData } from 'src/hooks/use-product-list';

import { ROLES } from 'src/api/roles-config';

import { TriStateCheckbox } from './tristate-checkbox';
// import { TriStateCheckbox } from './tristate-checkbox';
import MultiAttributeAutocomplete from './filters-multiselect';
import FavouritesMultiselect, { FavouritesCollectionOption } from './favourites-multiselect';

interface FilterSidebarProps {
  open: boolean;
  onClose: () => void;
  onApplyFilters: (data: SearchAndFilterData) => void;
  onClearFilters: () => void;
  linkedSites?: Array<{ id: string; name: string }>;
  favouritesCollections?: FavouritesCollectionOption[];
  isLoading?: boolean;
  filterData: SearchAndFilterData;
  onChange: (field: keyof SearchAndFilterData, value: any) => void;
}

export default function ProductFilterSidebar({
  open,
  onClose,
  onApplyFilters,
  onClearFilters,
  linkedSites = [],
  favouritesCollections = [],
  isLoading = false,
  filterData,
  onChange,
}: FilterSidebarProps) {
  const handleApplyFilters = () => {
    onApplyFilters(filterData);
    onClose();
  };

  const handleClearFilters = () => {
    onClearFilters();
    onClose();
  };

  return (
    <FilterSidebar
      open={open}
      onClose={onClose}
      handleApplyFilters={handleApplyFilters}
      handleClearFilters={handleClearFilters}
    >
      <Stack spacing={3}>
        {/* <Box>
          <Stack direction="column" spacing={2}>
            <TriStateCheckbox
              name="isDirectFromProducer"
              label="Direct From Producer"
              tooltip="Show products that are delivered by the producer themselves"
              value={filterData.isDirectFromProducer}
              onChange={(value) => onChange('isDirectFromProducer', value)}
            />
            <TriStateCheckbox
              name="isSmeProducer"
              tooltip="Show products that are created by small or medium sized producers"
              label="SME Producer"
              value={filterData.isSmeProducer}
              onChange={(value) => onChange('isSmeProducer', value)}
            />
          </Stack>
        </Box> */}

        <Box>
          <Typography variant="subtitle1" gutterBottom mb={3}>
            Favourites
          </Typography>
          <FavouritesMultiselect
            options={favouritesCollections}
            value={filterData.favouriteCollectionIds || []}
            onChange={(ids) => onChange('favouriteCollectionIds', ids)}
          />
        </Box>

        <Box>
          <Typography variant="subtitle1" gutterBottom mb={3}>
            Price Type
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="productType-label">Price Type</InputLabel>
            <Select
              labelId="productType-label"
              value={filterData.productType || ''}
              label="Price Type"
              onChange={(e) => onChange('productType', e.target.value || null)}
              sx={{ width: '100%' }}
            >
              <MenuItem value="">No price filter</MenuItem>
              <MenuItem value="promotion">Promotional</MenuItem>
              <RoleRestrictedMenuItem value="surplus" requiredRoles={[ROLES.CHARITY_BUYER]}>
                Surplus
              </RoleRestrictedMenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box>
          <Typography variant="subtitle1" gutterBottom mb={3}>
            Producer Locality
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="producerDistance-label">Maximum Distance</InputLabel>
            <Select
              labelId="producerDistance-label"
              value={filterData.producerDistance}
              label="Maximum Distance"
              onChange={(e) => onChange('producerDistance', e.target.value)}
              sx={{ width: '100%' }}
            >
              <MenuItem value="">No distance filter</MenuItem>
              <MenuItem value="5">5 miles</MenuItem>
              <MenuItem value="10">10 miles</MenuItem>
              <MenuItem value="50">50 miles</MenuItem>
              <MenuItem value="100">100 miles</MenuItem>
              <MenuItem value="200">200 miles</MenuItem>
              <MenuItem value="300">300 miles</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box>
          <Typography variant="subtitle1" gutterBottom mb={3}>
            Supplier
          </Typography>
          <Autocomplete
            value={linkedSites.find((site) => site.id === filterData.distributorSiteId) || null}
            onChange={(_event, newValue) => {
              onChange('distributorSiteId', newValue?.id || null);
            }}
            options={linkedSites}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Supplier Site"
                placeholder="Search Supplier Site"
              />
            )}
            sx={{ width: '100%' }}
          />
        </Box>

        <Box>
          <Typography variant="subtitle1" gutterBottom mb={3}>
            Product Attributes
          </Typography>
          <MultiAttributeAutocomplete filterData={filterData} onChange={onChange} />
          <TriStateCheckbox
            name="airfreighted"
            label="Airfreighted"
            tooltip="Filter by airfreighted products"
            value={filterData.airfreighted}
            onChange={(value) => onChange('airfreighted', value)}
          />
        </Box>
      </Stack>
    </FilterSidebar>
  );
}
